import { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { toast } from "sonner";
import { arrayMove } from "@dnd-kit/sortable";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const EMPTY_WORKOUT_FORM = {
  title: "",
  program_id: "",
  phase_id: "",
  day_of_week: "",
  description: "",
  exercises: [],
};
const EMPTY_PROGRAM_FORM = {
  title: "",
  phase_id: "",
  description: "",
  start_date: "",
  end_date: "",
};

/**
 * Centralizes all data + CRUD logic for the WorkoutsTab so the UI component
 * stays focused on layout. The hook owns fetching, programs/workouts state,
 * and the handlers wired to ProgramDialog/WorkoutDialog/Copy/Template flows.
 */
export default function useWorkoutsTab(clientId) {
  const [workouts, setWorkouts] = useState([]);
  const [programs, setPrograms] = useState([]);
  const [phases, setPhases] = useState([]);
  const [exerciseLibrary, setExerciseLibrary] = useState([]);
  const [userMappings, setUserMappings] = useState({ extra_synonyms: [], custom_groups: [] });
  const [expandedPrograms, setExpandedPrograms] = useState({});

  const [showWorkoutDialog, setShowWorkoutDialog] = useState(false);
  const [showProgramDialog, setShowProgramDialog] = useState(false);
  const [editingWorkout, setEditingWorkout] = useState(null);
  const [editingProgram, setEditingProgram] = useState(null);
  const [copyingWorkout, setCopyingWorkout] = useState(null);
  const [copyTargetProgram, setCopyTargetProgram] = useState("none");
  const [showFromTemplate, setShowFromTemplate] = useState(false);
  const [fromTemplateProgramId, setFromTemplateProgramId] = useState("");

  const [workoutForm, setWorkoutForm] = useState(EMPTY_WORKOUT_FORM);
  const [programForm, setProgramForm] = useState(EMPTY_PROGRAM_FORM);

  const fetchData = useCallback(async () => {
    try {
      const [wRes, pRes, phRes, exRes, mRes] = await Promise.all([
        axios.get(`${API}/clients/${clientId}/workouts`),
        axios.get(`${API}/clients/${clientId}/programs`),
        axios.get(`${API}/clients/${clientId}/phases`),
        axios.get(`${API}/exercises`),
        axios
          .get(`${API}/settings/muscle-mappings`)
          .catch(() => ({ data: { extra_synonyms: [], custom_groups: [] } })),
      ]);
      setWorkouts(wRes.data);
      setPrograms(pRes.data);
      setPhases(phRes.data);
      setExerciseLibrary(exRes.data || []);
      setUserMappings(mRes.data || { extra_synonyms: [], custom_groups: [] });

      // All programs collapsed by default — coach can expand on demand
      const expanded = {};
      pRes.data.forEach((p) => {
        expanded[p.id] = false;
      });
      setExpandedPrograms((prev) => {
        const merged = { ...expanded };
        // Preserve user's explicit toggles across re-fetches
        Object.keys(prev).forEach((k) => {
          if (k in merged) merged[k] = prev[k];
        });
        return merged;
      });
    } catch (err) {
      console.error("[WorkoutsTab] fetchData failed", err);
      toast.error("Errore nel caricamento delle schede");
    }
  }, [clientId]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const getPhaseTitle = useCallback(
    (phaseId) => phases.find((p) => p.id === phaseId)?.title || "",
    [phases]
  );

  const isPhaseCompleted = useCallback(
    (phaseId) => {
      if (!phaseId) return false;
      const phase = phases.find((p) => p.id === phaseId);
      return phase?.status === "completata";
    },
    [phases]
  );

  const isProgramCompleted = (program) =>
    program?.status === "completata" || isPhaseCompleted(program?.phase_id);

  const toggleProgram = (id) => {
    setExpandedPrograms((prev) => ({ ...prev, [id]: !prev[id] }));
  };

  // ============ PROGRAM HANDLERS ============
  const handleSaveProgram = async () => {
    try {
      if (editingProgram) {
        await axios.put(`${API}/clients/${clientId}/programs/${editingProgram.id}`, programForm);
        toast.success("Programma aggiornato");
      } else {
        await axios.post(`${API}/clients/${clientId}/programs`, {
          ...programForm,
          order: programs.length,
        });
        toast.success("Programma creato");
      }
      setShowProgramDialog(false);
      setEditingProgram(null);
      setProgramForm(EMPTY_PROGRAM_FORM);
      fetchData();
    } catch (err) {
      console.error("[WorkoutsTab] saveProgram failed", err);
      toast.error("Errore nel salvataggio");
    }
  };

  const handleDeleteProgram = async (programId) => {
    if (!window.confirm("Eliminare questo programma? Le schede al suo interno verranno scollegate.")) return;
    try {
      await axios.delete(`${API}/clients/${clientId}/programs/${programId}`);
      toast.success("Programma eliminato");
      fetchData();
    } catch (err) {
      console.error("[WorkoutsTab] deleteProgram failed", err);
      toast.error("Errore nell'eliminazione");
    }
  };

  const handleToggleVisibility = async (program) => {
    const next = program.visible_to_client === false;
    try {
      await axios.put(`${API}/clients/${clientId}/programs/${program.id}`, {
        visible_to_client: next,
      });
      toast.success(next ? "Programma visibile al cliente" : "Programma nascosto al cliente");
      fetchData();
    } catch (err) {
      console.error("[WorkoutsTab] toggleVisibility failed", err);
      toast.error("Errore nell'aggiornamento");
    }
  };

  const openEditProgram = (prog) => {
    setEditingProgram(prog);
    setProgramForm({
      title: prog.title,
      phase_id: prog.phase_id || "",
      description: prog.description || "",
      start_date: prog.start_date || "",
      end_date: prog.end_date || "",
    });
    setShowProgramDialog(true);
  };

  const openNewProgram = () => {
    setEditingProgram(null);
    setProgramForm(EMPTY_PROGRAM_FORM);
    setShowProgramDialog(true);
  };

  // ============ WORKOUT HANDLERS ============
  const handleSaveWorkout = async () => {
    try {
      const payload = {
        ...workoutForm,
        exercises: workoutForm.exercises.map((e) => ({
          ...e,
          sets: parseInt(e.sets, 10) || 0,
          rest_seconds: parseInt(e.rest_seconds, 10) || 0,
        })),
      };
      if (editingWorkout) {
        await axios.put(`${API}/clients/${clientId}/workouts/${editingWorkout.id}`, payload);
        toast.success("Scheda aggiornata");
      } else {
        await axios.post(`${API}/clients/${clientId}/workouts`, payload);
        toast.success("Scheda creata");
      }
      setShowWorkoutDialog(false);
      setEditingWorkout(null);
      setWorkoutForm(EMPTY_WORKOUT_FORM);
      fetchData();
    } catch (err) {
      console.error("[WorkoutsTab] saveWorkout failed", err);
      toast.error("Errore nel salvataggio");
    }
  };

  const handleDeleteWorkout = async (workoutId) => {
    if (!window.confirm("Eliminare questa scheda?")) return;
    try {
      await axios.delete(`${API}/clients/${clientId}/workouts/${workoutId}`);
      toast.success("Scheda eliminata");
      fetchData();
    } catch (err) {
      console.error("[WorkoutsTab] deleteWorkout failed", err);
      toast.error("Errore nell'eliminazione");
    }
  };

  const openEditWorkout = (workout) => {
    setEditingWorkout(workout);
    setWorkoutForm({
      title: workout.title,
      program_id: workout.program_id || "",
      phase_id: workout.phase_id || "",
      day_of_week: workout.day_of_week || "",
      description: workout.description || "",
      exercises: workout.exercises || [],
    });
    setShowWorkoutDialog(true);
  };

  const openNewWorkout = (programId = "") => {
    setEditingWorkout(null);
    setWorkoutForm({ ...EMPTY_WORKOUT_FORM, program_id: programId });
    setShowWorkoutDialog(true);
  };

  const openCopyWorkout = (workout) => {
    setCopyingWorkout(workout);
    setCopyTargetProgram(workout.program_id || "none");
  };

  const handleCopyWorkout = async () => {
    if (!copyingWorkout) return;
    try {
      const payload = {
        title: `${copyingWorkout.title} (copia)`,
        program_id: copyTargetProgram === "none" ? "" : copyTargetProgram,
        phase_id: copyingWorkout.phase_id || "",
        day_of_week: copyingWorkout.day_of_week || "",
        description: copyingWorkout.description || "",
        exercises: copyingWorkout.exercises || [],
      };
      await axios.post(`${API}/clients/${clientId}/workouts`, payload);
      const targetName = copyTargetProgram === "none"
        ? "Senza programma"
        : programs.find((p) => p.id === copyTargetProgram)?.title || "";
      toast.success(`Scheda copiata in "${targetName}"`);
      setCopyingWorkout(null);
      fetchData();
    } catch (err) {
      console.error("[WorkoutsTab] copyWorkout failed", err);
      toast.error("Errore nella copia");
    }
  };

  const handleSaveAsTemplate = async (workout) => {
    const title = window.prompt("Titolo per il template:", workout.title);
    if (!title || !title.trim()) return;
    try {
      await axios.post(
        `${API}/workout-templates/from-workout/${workout.id}?title_override=${encodeURIComponent(title.trim())}`
      );
      toast.success("Scheda salvata come template");
    } catch (err) {
      console.error("[WorkoutsTab] saveAsTemplate failed", err);
      toast.error("Errore nel salvataggio del template");
    }
  };

  const openFromTemplate = (programId = "") => {
    setFromTemplateProgramId(programId);
    setShowFromTemplate(true);
  };

  const handleInstantiateTemplate = async (template) => {
    try {
      await axios.post(`${API}/clients/${clientId}/workouts/from-template`, {
        template_id: template.id,
        program_id: fromTemplateProgramId || "",
      });
      toast.success(`Template "${template.title}" applicato`);
      setShowFromTemplate(false);
      fetchData();
    } catch (err) {
      console.error("[WorkoutsTab] instantiateTemplate failed", err);
      toast.error("Errore nell'applicazione del template");
    }
  };

  const handleWorkoutDragEnd = async (programId, event) => {
    const { active, over } = event;
    if (!over || active.id === over.id) return;
    const programWorkouts = workouts
      .filter((w) => (programId ? w.program_id === programId : !w.program_id))
      .sort((a, b) => (a.order || 0) - (b.order || 0));
    const oldIndex = programWorkouts.findIndex((w) => w.id === active.id);
    const newIndex = programWorkouts.findIndex((w) => w.id === over.id);
    if (oldIndex === -1 || newIndex === -1) return;
    const reordered = arrayMove(programWorkouts, oldIndex, newIndex);
    // Optimistic update
    const newWorkouts = workouts.map((w) => {
      const idx = reordered.findIndex((r) => r.id === w.id);
      return idx >= 0 ? { ...w, order: idx } : w;
    });
    setWorkouts(newWorkouts);
    // Persist
    try {
      await axios.put(`${API}/clients/${clientId}/workouts/reorder`, {
        workouts: reordered.map((w, i) => ({ id: w.id, order: i })),
      });
    } catch (err) {
      console.error("[WorkoutsTab] reorder failed", err);
      toast.error("Errore nel riordinamento");
      fetchData();
    }
  };

  return {
    // data
    workouts,
    programs,
    phases,
    exerciseLibrary,
    userMappings,
    expandedPrograms,
    // dialogs
    showWorkoutDialog,
    setShowWorkoutDialog,
    showProgramDialog,
    setShowProgramDialog,
    editingWorkout,
    editingProgram,
    copyingWorkout,
    copyTargetProgram,
    setCopyTargetProgram,
    showFromTemplate,
    setShowFromTemplate,
    fromTemplateProgramId,
    setFromTemplateProgramId,
    workoutForm,
    setWorkoutForm,
    programForm,
    setProgramForm,
    // helpers
    getPhaseTitle,
    isProgramCompleted,
    toggleProgram,
    fetchData,
    // handlers
    handleSaveProgram,
    handleDeleteProgram,
    handleToggleVisibility,
    openEditProgram,
    openNewProgram,
    handleSaveWorkout,
    handleDeleteWorkout,
    openEditWorkout,
    openNewWorkout,
    openCopyWorkout,
    handleCopyWorkout,
    handleSaveAsTemplate,
    openFromTemplate,
    handleInstantiateTemplate,
    handleWorkoutDragEnd,
    setCopyingWorkout,
  };
}
